import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Eye, Loader2, Printer, Upload } from "lucide-react";

type FormationDoc = {
  id: string;
  instrument_slug: string;
  status: string;
  text_pdf_path: string | null;
  rasterized_pdf_path: string | null;
  page_count: number | null;
  edition: string | null;
  reject_reason: string | null;
};

type MemberRow = { id: string; first_name: string; last_name: string; una_name?: string | null };

const INSTRUMENT_LABEL: Record<string, string> = {
  una_organization: "UNA Organization",
  ca_sub: "California Statement",
  una_constitution: "UNA Constitution",
  una_docs: "UNA Docs",
};

const HUMAN: Record<string, string> = {
  queued: "Queued",
  rendering: "Rendering",
  in_review: "In review",
  approved: "Approved",
  rejected: "Sent back",
};

/** Extra Record documents that are uploaded by the team, not generated. */
const EXTRA_SLOTS: { key: "cp575e_url" | "ca_reg_url"; label: string; fileHint: string }[] = [
  { key: "cp575e_url", label: "CP575E notice", fileHint: "The IRS confirmation you received." },
  { key: "ca_reg_url", label: "CA (Reg)", fileHint: "The state registration certificate." },
];

export function PrintPacketDialog({
  member,
  formationId,
  onClose,
}: {
  member: MemberRow;
  formationId: string | null;
  onClose: () => void;
}) {
  const [docs, setDocs] = useState<FormationDoc[]>([]);
  const [extra, setExtra] = useState<{ cp575e_url: string | null; ca_reg_url: string | null }>({
    cp575e_url: null,
    ca_reg_url: null,
  });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [{ data: docRows }, { data: record }] = await Promise.all([
      (supabase as any)
        .from("formation_documents")
        .select("id, instrument_slug, status, text_pdf_path, rasterized_pdf_path, page_count, edition, reject_reason")
        .eq("formation_id", formationId ?? "00000000-0000-0000-0000-000000000000")
        .order("instrument_slug", { ascending: true }),
      (supabase as any)
        .from("member_records")
        .select("cp575e_url, ca_reg_url")
        .eq("id", member.id)
        .maybeSingle(),
    ]);
    setDocs((docRows ?? []) as FormationDoc[]);
    setExtra({
      cp575e_url: record?.cp575e_url ?? null,
      ca_reg_url: record?.ca_reg_url ?? null,
    });
    setLoading(false);
  }, [formationId, member.id]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Open a Record file in a new tab so the browser print dialog works on a phone. */
  const openSigned = async (path: string | null) => {
    if (!path) return;
    const { data } = await (supabase as any)
      .storage.from("documents")
      .createSignedUrl(String(path), 3600);
    if (data?.signedUrl) window.open(data.signedUrl, "_blank");
    else toast.error("That file could not be opened right now.");
  };

  const openSlot = async (key: "cp575e_url" | "ca_reg_url") => {
    await openSigned(extra[key]);
  };

  const uploadSlot = async (key: "cp575e_url" | "ca_reg_url", file: File | undefined) => {
    if (!file) return;
    setBusy(key);
    const safe = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
    const path = `member_records/${member.id}/${key}/${Date.now()}_${safe}`;
    const { error: upErr } = await (supabase as any)
      .storage.from("documents")
      .upload(path, file, { contentType: file.type, upsert: true });
    if (upErr) {
      toast.error(upErr.message ?? "The file could not be uploaded.");
      setBusy(null);
      return;
    }
    const { error: patchErr } = await (supabase as any)
      .from("member_records")
      .update({ [key]: path })
      .eq("id", member.id);
    setBusy(null);
    if (patchErr) {
      toast.error(patchErr.message ?? "The file was uploaded but the record was not updated.");
      return;
    }
    toast.success("Saved to the Record");
    void load();
  };

  const name = member.una_name || `${member.first_name || ""} ${member.last_name || ""}`.trim() || member.id;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[85dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Print packet</DialogTitle>
          <DialogDescription>
            {name}. The approved papers for this Record, plus the team uploads.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center gap-2 py-6 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading the Record…
          </div>
        ) : (
          <div className="space-y-3">
            {docs.length === 0 && (
              <div className="rounded-md border p-4 text-sm text-muted-foreground">
                No generated papers yet. They appear here after they clear review.
              </div>
            )}

            {docs.map((d) => {
              const path = d.rasterized_pdf_path || d.text_pdf_path;
              return (
                <div key={d.id} className="rounded-md border p-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-medium">{INSTRUMENT_LABEL[d.instrument_slug] ?? d.instrument_slug}</div>
                    <Badge variant={d.status === "approved" ? "secondary" : "outline"}>
                      {HUMAN[d.status] ?? d.status}
                    </Badge>
                  </div>
                  {d.edition && <div className="text-xs text-muted-foreground">Edition {d.edition}</div>}
                  {d.page_count != null && (
                    <div className="text-xs text-muted-foreground">{d.page_count} pages</div>
                  )}
                  {d.status === "rejected" && d.reject_reason && (
                    <div className="mt-1 rounded bg-destructive/10 px-2 py-1 text-xs text-destructive">
                      Sent back: {d.reject_reason}
                    </div>
                  )}
                  <div className="mt-2 flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 text-xs"
                      disabled={d.status !== "approved" || !path || busy !== null}
                      onClick={() => openSigned(path)}
                    >
                      {busy === `view_${d.id}` ? <Loader2 className="h-3 w-3 animate-spin" /> : <Eye className="h-3 w-3" />}
                      View
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 text-xs"
                      disabled={d.status !== "approved" || !path}
                      onClick={() => openSigned(path)}
                    >
                      <Printer className="h-3 w-3" /> Print
                    </Button>
                  </div>
                </div>
              );
            })}

            <div className="mt-2 space-y-2 rounded-md border p-3">
              <div className="text-sm font-semibold">Team uploads</div>
              {EXTRA_SLOTS.map((slot) => (
                <div key={slot.key} className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <Label className="text-xs">{slot.label}</Label>
                    <div className="text-xs text-muted-foreground">{slot.fileHint}</div>
                    {extra[slot.key] && (
                      <div className="truncate text-xs text-muted-foreground">Saved to the Record</div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {extra[slot.key] && (
                      <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => openSlot(slot.key)}>
                        Open
                      </Button>
                    )}
                    <label className="inline-flex h-8 items-center gap-1 rounded-md border px-2 text-xs cursor-pointer">
                      <Upload className="h-3 w-3" /> Upload
                      <input
                        type="file"
                        accept="application/pdf"
                        className="hidden"
                        disabled={busy !== null}
                        onChange={(e) => void uploadSlot(slot.key, e.target.files?.[0])}
                      />
                    </label>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
